import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import API from '../api/axios';
import TaskModal from '../components/TaskModal';
import { useUserPreferences } from '../context/UserPreferencesContext';

// ---------- date helpers ----------
const pad2 = (n) => String(n).padStart(2, '0');

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function parseISO(iso) {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d);
}

function toISO(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

function addDaysISO(iso, n) {
  const d = parseISO(iso);
  d.setDate(d.getDate() + n);
  return toISO(d);
}

const DOW_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DOW_SHORT = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const MONTH_LONG = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

function formatLongDate(iso) {
  const d = parseISO(iso);
  return `${DOW_LONG[d.getDay()]}, ${MONTH_LONG[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
}

// ---------- time helpers ----------
function timeToMinutes(t) {
  if (!t) return null;
  const [h, m] = t.slice(0, 5).split(':').map(Number);
  return h * 60 + m;
}

function formatTime12(t) {
  if (!t) return '';
  const [h, m] = t.slice(0, 5).split(':').map(Number);
  const suffix = h < 12 ? 'AM' : 'PM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${pad2(m)} ${suffix}`;
}

const SCHEDULE_START_HOUR = 6;   // 6 AM
const SCHEDULE_END_HOUR = 22;    // 10 PM
const HOUR_HEIGHT = 56;          // px per hour
const SCHEDULE_HEIGHT = (SCHEDULE_END_HOUR - SCHEDULE_START_HOUR) * HOUR_HEIGHT;

// ---------- main page ----------
export default function PlannerPage() {
  const { preferences } = useUserPreferences();
  const eventsEnabled = preferences.plannerEventsEnabled !== false;

  const [date, setDate] = useState(todayISO());
  const [tasks, setTasks] = useState([]);
  const [events, setEvents] = useState([]);
  const [note, setNote] = useState('');
  const [noteSavedAt, setNoteSavedAt] = useState(null);
  const [editEvent, setEditEvent] = useState(null); // null | {} for new | event obj for edit
  const [editTask, setEditTask] = useState(null);
  const [folders, setFolders] = useState([]);
  const [tags, setTags] = useState([]);
  const [showCompleted, setShowCompleted] = useState(() =>
    localStorage.getItem('focusly-daily-show-completed') === 'true'
  );
  const noteHydratedRef = useRef(false);

  useEffect(() => {
    localStorage.setItem('focusly-daily-show-completed', String(showCompleted));
  }, [showCompleted]);

  useEffect(() => {
    API.get('/folders').then(({ data }) => setFolders(data));
    API.get('/tags').then(({ data }) => setTags(data));
  }, []);

  const tasksUrl = useMemo(() => {
    let url = `/tasks/by-date?date=${date}`;
    if (showCompleted) url += '&include_completed=true';
    return url;
  }, [date, showCompleted]);

  const refetchAll = useCallback(async () => {
    const requests = [
      API.get(tasksUrl),
      API.get(`/daily-notes?date=${date}`),
    ];
    if (eventsEnabled) requests.push(API.get(`/events?date=${date}`));
    const [taskRes, noteRes, eventRes] = await Promise.all(requests);
    setTasks(taskRes.data);
    noteHydratedRef.current = false;
    setNote(noteRes.data?.content || '');
    setEvents(eventsEnabled ? eventRes.data : []);
  }, [date, eventsEnabled, tasksUrl]);

  useEffect(() => { refetchAll(); }, [refetchAll]);

  // A task dragged onto a folder in the sidebar tree may change its folder —
  // refetch so folder labels stay current.
  useEffect(() => {
    const onChanged = () => refetchAll();
    window.addEventListener('focusly:tasks-changed', onChanged);
    return () => window.removeEventListener('focusly:tasks-changed', onChanged);
  }, [refetchAll]);

  const [noteError, setNoteError] = useState(null);

  // Debounced auto-save for daily notes.
  useEffect(() => {
    if (!noteHydratedRef.current) {
      noteHydratedRef.current = true;
      return;
    }
    const t = setTimeout(async () => {
      try {
        await API.put('/daily-notes', { date, content: note });
        setNoteSavedAt(new Date());
        setNoteError(null);
      } catch (err) {
        setNoteError(err.response?.data?.error || err.message || 'Save failed');
      }
    }, 600);
    return () => clearTimeout(t);
  }, [note, date]);

  const goPrev = () => setDate(addDaysISO(date, -1));
  const goNext = () => setDate(addDaysISO(date, 1));
  const goToday = () => setDate(todayISO());

  const refreshEvents = async () => {
    const { data } = await API.get(`/events?date=${date}`);
    setEvents(data);
  };

  const refreshTasks = async () => {
    const { data } = await API.get(tasksUrl);
    setTasks(data);
  };

  const handleTaskSave = async (form) => {
    await API.put(`/tasks/${editTask.id}`, form);
    setEditTask(null);
    await refreshTasks();
  };

  const handleTaskDelete = async (id) => {
    if (!window.confirm('Delete this task?')) return;
    await API.delete(`/tasks/${id}`);
    setEditTask(null);
    await refreshTasks();
  };

  return (
    <div className="container-fluid py-3 planner-page" style={{ maxWidth: 1400 }}>
      <PlannerHeader
        date={date}
        onPrev={goPrev}
        onNext={goNext}
        onToday={goToday}
        onPickDate={setDate}
      />

      {eventsEnabled ? (
        <div className="row g-3 mt-1">
          <div className="col-lg-7">
            <ScheduleColumn
              events={events}
              onCreate={(startHour) => setEditEvent({ event_date: date, start_time: `${pad2(startHour)}:00` })}
              onEdit={(ev) => setEditEvent(ev)}
            />
          </div>
          <div className="col-lg-5">
            <MiniMonth date={date} onSelectDate={setDate} />
            <PriorityTaskList
              tasks={tasks}
              onEditTask={setEditTask}
              showCompleted={showCompleted}
              onToggleCompleted={() => setShowCompleted((v) => !v)}
            />
            <DailyNotesPane note={note} onChange={setNote} savedAt={noteSavedAt} error={noteError} />
          </div>
        </div>
      ) : (
        <div className="row g-3 mt-1" style={{ height: 'calc(100vh - 140px)', minHeight: 400 }}>
          <div className="col-lg-5 d-flex flex-column">
            <MiniMonth date={date} onSelectDate={setDate} />
            <PriorityTaskList
              tasks={tasks}
              onEditTask={setEditTask}
              showCompleted={showCompleted}
              onToggleCompleted={() => setShowCompleted((v) => !v)}
              fill
            />
          </div>
          <div className="col-lg-7 d-flex flex-column">
            <DailyNotesPane note={note} onChange={setNote} savedAt={noteSavedAt} error={noteError} fill />
          </div>
        </div>
      )}

      {editEvent && (
        <EventModal
          event={editEvent}
          onClose={() => setEditEvent(null)}
          onSaved={async () => {
            setEditEvent(null);
            await refreshEvents();
          }}
          onDateChanged={(newIso) => {
            setEditEvent(null);
            setDate(newIso);
          }}
        />
      )}

      {editTask && (
        <TaskModal
          task={editTask}
          onSave={handleTaskSave}
          onDelete={handleTaskDelete}
          onClose={() => setEditTask(null)}
          folders={folders}
          currentFolderId={editTask?.folder_id}
          tags={tags}
        />
      )}
    </div>
  );
}

// ---------- header ----------
function PlannerHeader({ date, onPrev, onNext, onToday, onPickDate }) {
  return (
    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 pb-2 border-bottom">
      <div className="d-flex align-items-center gap-2">
        <button className="btn btn-outline-secondary btn-sm" onClick={onPrev} aria-label="Previous day">
          ‹
        </button>
        <button className="btn btn-outline-secondary btn-sm" onClick={onNext} aria-label="Next day">
          ›
        </button>
        <button className="btn btn-outline-primary btn-sm" onClick={onToday}>Today</button>
        <input
          type="date"
          className="form-control form-control-sm"
          style={{ width: 'auto' }}
          value={date}
          onChange={(e) => e.target.value && onPickDate(e.target.value)}
        />
      </div>
      <div className="text-end">
        <h3 className="mb-0" style={{ fontFamily: 'Georgia, serif' }}>{formatLongDate(date)}</h3>
      </div>
    </div>
  );
}

// ---------- schedule (hourly grid with positioned events) ----------
function ScheduleColumn({ events, onCreate, onEdit }) {
  const hours = [];
  for (let h = SCHEDULE_START_HOUR; h < SCHEDULE_END_HOUR; h++) hours.push(h);

  // Position events absolutely. Default duration is 60 minutes if end_time missing.
  const positioned = events.map((ev) => {
    const startMin = timeToMinutes(ev.start_time) ?? SCHEDULE_START_HOUR * 60;
    const endMin = timeToMinutes(ev.end_time) ?? startMin + 60;
    const top = ((startMin - SCHEDULE_START_HOUR * 60) / 60) * HOUR_HEIGHT;
    const height = Math.max(((endMin - startMin) / 60) * HOUR_HEIGHT, 24);
    return { ev, top, height };
  });

  return (
    <div className="border rounded bg-body" style={{ overflow: 'hidden' }}>
      <div className="px-3 py-2 border-bottom d-flex justify-content-between align-items-center">
        <strong style={{ fontFamily: 'Georgia, serif' }}>Schedule</strong>
        <small className="text-muted">Click a slot to add</small>
      </div>
      <div style={{ position: 'relative', height: SCHEDULE_HEIGHT }}>
        {hours.map((h, i) => (
          <div
            key={h}
            onClick={() => onCreate(h)}
            style={{
              position: 'absolute',
              top: i * HOUR_HEIGHT,
              left: 0,
              right: 0,
              height: HOUR_HEIGHT,
              borderBottom: '2px solid var(--bs-border-color)',
              cursor: 'pointer',
              display: 'flex',
            }}
            className="hover-bg"
          >
            <div
              style={{
                width: 64,
                flexShrink: 0,
                borderRight: '2px solid var(--bs-border-color)',
                padding: '4px 8px',
                fontSize: '0.75rem',
                color: 'var(--bs-secondary-color)',
                fontFamily: 'Georgia, serif',
              }}
            >
              {h % 12 === 0 ? 12 : h % 12}:00 {h < 12 ? 'AM' : 'PM'}
            </div>
          </div>
        ))}
        {positioned.map(({ ev, top, height }) => (
          <div
            key={ev.id}
            onClick={(e) => { e.stopPropagation(); onEdit(ev); }}
            style={{
              position: 'absolute',
              top,
              left: 70,
              right: 8,
              height,
              backgroundColor: ev.color || '#0d6efd',
              color: '#fff',
              borderRadius: 4,
              padding: '2px 6px',
              fontSize: '0.85rem',
              cursor: 'pointer',
              overflow: 'hidden',
              boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
            }}
          >
            <div className="fw-semibold text-truncate">{ev.title}</div>
            <div style={{ fontSize: '0.7rem', opacity: 0.9 }}>
              {formatTime12(ev.start_time)}{ev.end_time ? ` – ${formatTime12(ev.end_time)}` : ''}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------- prioritized task list ----------
const priorityRank = { High: 0, Medium: 1, Low: 2 };
const priorityBadgeClass = { High: 'bg-danger', Medium: 'bg-warning text-dark', Low: 'bg-success' };

function PriorityTaskList({ tasks, onEditTask, showCompleted, onToggleCompleted, fill }) {
  const numbered = useMemo(() => {
    const sorted = [...tasks].sort((a, b) =>
      (priorityRank[a.priority] ?? 99) - (priorityRank[b.priority] ?? 99)
    );
    return sorted.map((t, i) => ({ ...t, _label: String(i + 1) }));
  }, [tasks]);

  return (
    <div
      className={`border rounded bg-body ${fill ? '' : 'mb-3'}`}
      style={fill ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } : undefined}
    >
      <div className="px-3 py-2 border-bottom d-flex justify-content-between align-items-center">
        <strong style={{ fontFamily: 'Georgia, serif' }}>Daily Tasks</strong>
        {onToggleCompleted && (
          <button
            type="button"
            className={`btn btn-sm ${showCompleted ? 'btn-primary' : 'btn-outline-secondary'}`}
            onClick={onToggleCompleted}
            title={showCompleted ? 'Hide completed tasks' : 'Show completed tasks'}
          >
            {showCompleted ? '✓ Completed' : 'Show Completed'}
          </button>
        )}
      </div>
      <div className="p-2" style={fill ? { flex: 1, minHeight: 0, overflowY: 'auto' } : undefined}>
        {numbered.length === 0 ? (
          <div className="text-muted small px-2 py-3">No tasks scheduled for this day.</div>
        ) : (
          numbered.map((t) => {
            const isCompleted = t.status === 'Completed';
            return (
              <div
                key={t.id}
                className="d-flex align-items-center gap-2 px-2 py-1 border-bottom hover-bg"
                style={{ cursor: 'pointer' }}
                onClick={() => onEditTask && onEditTask(t)}
              >
                <span
                  className="text-muted align-middle"
                  draggable
                  onClick={(e) => e.stopPropagation()}
                  onDragStart={(e) => {
                    e.stopPropagation();
                    e.dataTransfer.setData('application/x-focusly-task', JSON.stringify([t.id]));
                    e.dataTransfer.setData('text/plain', t.name || '');
                    e.dataTransfer.effectAllowed = 'move';
                  }}
                  title="Drag onto a folder to move this task"
                  aria-label="Drag task to another folder"
                  role="button"
                  style={{ cursor: 'grab', fontSize: '0.95rem', lineHeight: 1 }}
                >
                  {'⠿'}
                </span>
                <span
                  className={`badge ${priorityBadgeClass[t.priority] || 'bg-secondary'}`}
                  style={{ minWidth: 32 }}
                >
                  {t._label}
                </span>
                <div className="flex-grow-1 text-truncate">
                  <div className="text-truncate d-flex align-items-center gap-2">
                    <span
                      className={`text-truncate ${isCompleted ? 'text-muted' : ''}`}
                      style={isCompleted ? { textDecoration: 'line-through' } : undefined}
                    >
                      {t.name}
                    </span>
                    {t.is_overdue && !isCompleted && (
                      <span className="badge bg-danger" style={{ fontSize: '0.6rem' }}>OVERDUE</span>
                    )}
                    {isCompleted && (
                      <span className="badge bg-success" style={{ fontSize: '0.6rem' }}>DONE</span>
                    )}
                  </div>
                  <small className="text-muted">{t.folder_name}</small>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

// ---------- daily notes ----------
function DailyNotesPane({ note, onChange, savedAt, error, fill }) {
  return (
    <div
      className={`border rounded bg-body ${fill ? '' : 'mb-3'}`}
      style={fill ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' } : undefined}
    >
      <div className="px-3 py-2 border-bottom d-flex justify-content-between align-items-center">
        <strong style={{ fontFamily: 'Georgia, serif' }}>Daily Notes</strong>
        {error
          ? <small className="text-danger">Save failed: {error}</small>
          : savedAt && <small className="text-muted">Saved {savedAt.toLocaleTimeString()}</small>}
      </div>
      <textarea
        className="form-control border-0"
        rows={fill ? undefined : 8}
        placeholder="Notes, thoughts, gratitude…"
        value={note}
        onChange={(e) => onChange(e.target.value)}
        style={fill
          ? { resize: 'none', borderRadius: 0, flex: 1, minHeight: 0 }
          : { resize: 'vertical', borderRadius: 0 }}
      />
    </div>
  );
}

// ---------- mini-month ----------
function MiniMonth({ date, onSelectDate }) {
  const selected = parseISO(date);
  const [view, setView] = useState({ year: selected.getFullYear(), month: selected.getMonth() });

  // Re-anchor to selected date when it changes externally.
  useEffect(() => {
    setView({ year: selected.getFullYear(), month: selected.getMonth() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  const firstOfMonth = new Date(view.year, view.month, 1);
  const startDayOfWeek = firstOfMonth.getDay();
  const daysInMonth = new Date(view.year, view.month + 1, 0).getDate();

  const cells = [];
  for (let i = 0; i < startDayOfWeek; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const todayStr = todayISO();

  const stepMonth = (delta) => {
    const m = view.month + delta;
    const y = view.year + Math.floor(m / 12);
    const mm = ((m % 12) + 12) % 12;
    setView({ year: y, month: mm });
  };

  return (
    <div className="border rounded bg-body mb-3">
      <div className="px-3 py-2 border-bottom d-flex justify-content-between align-items-center">
        <button className="btn btn-sm btn-link p-0" onClick={() => stepMonth(-1)} aria-label="Previous month">‹</button>
        <strong style={{ fontFamily: 'Georgia, serif' }}>
          {MONTH_LONG[view.month]} {view.year}
        </strong>
        <button className="btn btn-sm btn-link p-0" onClick={() => stepMonth(1)} aria-label="Next month">›</button>
      </div>
      <div className="p-2">
        <div className="d-grid" style={{ gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
          {DOW_SHORT.map((d, i) => (
            <div key={i} className="text-center text-muted" style={{ fontSize: '0.7rem' }}>{d}</div>
          ))}
          {cells.map((c, i) => {
            if (c === null) return <div key={i} />;
            const cellIso = `${view.year}-${pad2(view.month + 1)}-${pad2(c)}`;
            const isSelected = cellIso === date;
            const isToday = cellIso === todayStr;
            const cls = isSelected
              ? 'bg-primary text-white'
              : isToday
                ? 'border border-primary'
                : 'hover-bg';
            return (
              <div
                key={i}
                onClick={() => onSelectDate(cellIso)}
                className={`text-center rounded ${cls}`}
                style={{ cursor: 'pointer', padding: '4px 0', fontSize: '0.8rem' }}
              >
                {c}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ---------- event modal ----------
function EventModal({ event, onClose, onSaved, onDateChanged }) {
  const isNew = !event.id;
  const [form, setForm] = useState({
    event_date: event.event_date || todayISO(),
    start_time: (event.start_time || '09:00').slice(0, 5),
    end_time: event.end_time ? event.end_time.slice(0, 5) : '',
    title: event.title || '',
    description: event.description || '',
    color: event.color || '#0d6efd',
  });

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.title.trim()) return;
    const payload = {
      event_date: form.event_date,
      start_time: form.start_time,
      end_time: form.end_time || null,
      title: form.title.trim(),
      description: form.description || null,
      color: form.color,
    };
    if (isNew) {
      await API.post('/events', payload);
    } else {
      await API.put(`/events/${event.id}`, payload);
    }
    if (form.event_date !== event.event_date) {
      onDateChanged(form.event_date);
    } else {
      onSaved();
    }
  };

  const handleDelete = async () => {
    if (!event.id) return;
    if (!window.confirm('Delete this event?')) return;
    await API.delete(`/events/${event.id}`);
    onSaved();
  };

  return (
    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={onClose}>
      <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{isNew ? 'New Event' : 'Edit Event'}</h5>
            <button className="btn-close" onClick={onClose} />
          </div>
          <div className="modal-body">
            <div className="mb-2">
              <label className="form-label small">Title</label>
              <input
                autoFocus
                className="form-control"
                value={form.title}
                onChange={(e) => update('title', e.target.value)}
              />
            </div>
            <div className="row g-2 mb-2">
              <div className="col-12">
                <label className="form-label small">Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={form.event_date}
                  onChange={(e) => update('event_date', e.target.value)}
                />
              </div>
              <div className="col-6">
                <label className="form-label small">Start</label>
                <input
                  type="time"
                  className="form-control"
                  value={form.start_time}
                  onChange={(e) => update('start_time', e.target.value)}
                />
              </div>
              <div className="col-6">
                <label className="form-label small">End</label>
                <input
                  type="time"
                  className="form-control"
                  value={form.end_time}
                  onChange={(e) => update('end_time', e.target.value)}
                />
              </div>
            </div>
            <div className="mb-2">
              <label className="form-label small">Description</label>
              <textarea
                className="form-control"
                rows={3}
                value={form.description}
                onChange={(e) => update('description', e.target.value)}
              />
            </div>
            <div className="mb-2">
              <label className="form-label small">Color</label>
              <input
                type="color"
                className="form-control form-control-color"
                value={form.color}
                onChange={(e) => update('color', e.target.value)}
              />
            </div>
          </div>
          <div className="modal-footer">
            {!isNew && (
              <button className="btn btn-outline-danger me-auto" onClick={handleDelete}>Delete</button>
            )}
            <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button className="btn btn-primary" onClick={handleSave} disabled={!form.title.trim()}>
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
